import { Helmet } from 'react-helmet-async';
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { useTheme } from '@mui/material/styles';
import {
	Box,
	Stack,
	Button,
	Popover,
	Tooltip,
	MenuItem,
	Dialog,
	NativeSelect,
	Typography,
	IconButton,
	Grid,
} from '@mui/material';
import { DataGrid, Toolbar, gridDateComparator } from '@mui/x-data-grid';

// components
import Iconify from '../../../components/iconify';
import { ToolBarSearch, ToolBarColumnToggler, Container as DataGridContainer } from '../../../components/datagrid';
import { Status, PaymentStatus } from '../../../components/request';
import { type RequestProps, type Status as RequestStatus, Request } from '../../../models';
import { fDateTime } from '../../../utils/formatTime';
import { RequestListSkeleton } from '../../../components/shared';
import { useRole } from '../../../hooks/useRole';

import {
	useGetRequestsQuery,
	useDelRequestMutation,
	useUpdateRequestStatusMutation,
} from '../../../slices/apis/requestApi';
import type { GetRequestsResponseData } from '../../../slices/apis/apis.types';

// ----------------------------------------------------------------------

type DataGridRow = {
	id: RequestProps['id'];
	candidateName: string;
	phone: string;
	testCentre: string;
	assessmentDate: Date | null;
	paymentStatus: RequestProps['paymentStatus'];
	status: RequestProps['status'];
};

const findRequest = (requests: GetRequestsResponseData[], id: number) => {
	return requests.find((request) => Number(request.id) === id);
};

export default function UserContractorView() {
	const { isContractor } = useRole();
	const { data, isFetching } = useGetRequestsQuery({
		paginationModel: { page: 0, pageSize: 100 },
	});

	const requests = data?.data ?? [];

	const [requestEditable, setRequestEditable] = useState(false);
	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

	const theme = useTheme();
	const navigate = useNavigate();

	/*
		request deletion
	*/
	const [selectedRequest, setSelectedRequest] = useState<RequestProps | null>(null);
	const [delRequest, { isSuccess: isDelReqSuccess, isLoading: isDelReqLoading }] = useDelRequestMutation();
	const [requestDeleteConfirmDialogOpen, setRequestDeleteConfirmDialogOpen] = useState<boolean>(false);

	const handleNavigate = useCallback(
		(e: React.MouseEvent<HTMLButtonElement>) => {
			const route = e.currentTarget.getAttribute('data-route');
			if (route) {
				navigate(route);
			}
		},
		[navigate],
	);

	/*
		request status editing (CONTRACTOR only)
	*/
	const [statusEditingDialogOpen, setStatusEditingDialogOpen] = useState<boolean>(false);
	const [updateRequestStatus, { isSuccess: isUpdateReqStatusSuccess, isLoading: isUpdateReqStatusLoading }] =
		useUpdateRequestStatusMutation();

	const statusOptions: RequestStatus[] = isContractor ? Request.getAllowedEditableStatusContractor() : [];

	const handleOpenStatusEditingDialog = (e: React.MouseEvent<HTMLDivElement>) => {
		const requestId = Number(e.currentTarget.getAttribute('data-correlation-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			if (Request.isStatusEditingAllowed(request.status)) {
				setStatusEditingDialogOpen(true);
				setSelectedRequest(request);
			}
		}
	};

	const onStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
		if (!statusOptions.includes(e.target.value as RequestStatus)) {
			return;
		}

		setSelectedRequest({
			...selectedRequest,
			status: e.target.value,
		} as RequestProps);
	};

	const onStatusUpdate = () => {
		if (selectedRequest) {
			const { id, status } = selectedRequest;
			updateRequestStatus({
				id,
				status,
			});
		}
	};

	const handleCloseStatusEditingDialog = () => {
		setStatusEditingDialogOpen(false);
	};

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		const requestId = Number(event.currentTarget.getAttribute('data-request-id'));

		const request = findRequest(requests, requestId);

		if (request) {
			setAnchorEl(event.currentTarget);
			setSelectedRequest(request);
			setRequestEditable(Request.isEditable(request.status));
		}
	};

	const handleCloseMenu = () => {
		setAnchorEl(null);
	};

	const handleEditRequest = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/requests/${selectedRequest.id}/edit`);
		}
	};

	const handleOpenRequestDetail = () => {
		if (selectedRequest?.id) {
			navigate(`/dashboard/requests/${selectedRequest.id}/view`);
		}
	};

	/*
		del request
	*/
	const handleDeleteRequest = () => {
		setAnchorEl(null);
		setRequestDeleteConfirmDialogOpen(true);
	};

	const handleCloseRequestDeleteConfirmDialog = () => {
		setRequestDeleteConfirmDialogOpen(false);
	};

	const onRequestDeleteConfirm = () => {
		if (selectedRequest?.id) {
			delRequest(selectedRequest.id);
		}
	};

	useEffect(() => {
		if (isDelReqSuccess) {
			setRequestDeleteConfirmDialogOpen(false);
		}

		if (isUpdateReqStatusSuccess) {
			setStatusEditingDialogOpen(false);
			navigate('/dashboard/requests');
		}
	}, [isDelReqSuccess, isUpdateReqStatusSuccess, navigate]);

	return (
		<>
			<Helmet>
				<title> Requests - {process.env.REACT_APP_ORG} </title>
			</Helmet>
			<Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
				{!isContractor && (
					<Stack spacing={4} mb={3}>
						{!isFetching && (
							<Box>
								<Button
									variant="contained"
									color="primary"
									data-route="/dashboard/requests/new"
									onClick={handleNavigate}
									startIcon={<Iconify icon="eva:plus-fill" />}
								>
									New Single Request
								</Button>
							</Box>
						)}
					</Stack>
				)}

				{isFetching ? (
					<RequestListSkeleton />
				) : (
					<>
						<DataGridContainer>
							<DataGrid
								disableColumnFilter
								disableDensitySelector
								disableColumnSelector
								showToolbar
								columnHeaderHeight={48}
								slots={{
									toolbar: () => (
										<Toolbar>
											<Box sx={{ pl: 1 }}>
												<ToolBarSearch />
											</Box>
											<Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 0.5 }}>
												<Tooltip title="Columns">
													<ToolBarColumnToggler />
												</Tooltip>
											</Box>
										</Toolbar>
									),
								}}
								sx={{
									width: '100%',
									borderRadius: 3,
									'& .MuiDataGrid-columnHeaderTitle': {
										whiteSpace: 'normal',
										lineHeight: 1.4,
										fontSize: '0.9rem',
										color: 'grey',
									},
									'& .MuiDataGrid-columnHeader': {
										backgroundColor: theme.palette.grey[100],
									},
								}}
								columns={[
									{
										field: 'id',
										headerName: 'ID',
										resizable: false,
										sortable: false,
										flex: 0.25,
									},
									{
										field: 'candidateName',
										headerName: 'Name',
										resizable: false,
										sortable: false,
										flex: 0.8,
									},
									{
										field: 'phone',
										headerName: 'Phone',
										sortable: false,
										resizable: false,
										flex: 0.6,
									},
									{
										field: 'testCentre',
										headerName: 'Test Centre',
										resizable: false,
										sortable: false,
										flex: 0.7,
									},
									{
										field: 'assessmentDate',
										headerName: 'Asssessment Date',
										resizable: false,
										flex: 0.8,
										sortComparator: gridDateComparator,
										renderCell: (params) => {
											const assessmentDate = params.value ? fDateTime(params.value) : 'Pending';

											return (
												<Typography
													color="primary"
													variant="subtitle2"
													sx={{
														display: 'flex',
														alignItems: 'center',
														height: '100%',
														width: '100%',
													}}
												>
													{assessmentDate}
												</Typography>
											);
										},
									},
									{
										field: 'paymentStatus',
										headerName: 'Payment Status',
										resizable: false,
										flex: 0.8,
										renderCell: (params) => {
											const { row } = params;
											return <PaymentStatus paymentStatus={row.paymentStatus || 'Unknown'} />;
										},
									},
									{
										field: 'status',
										headerName: 'Request Status',
										resizable: false,
										flex: 0.8,
										renderCell: (params) => {
											const { row } = params;

											return (
												<Status
													status={row.status}
													clickable={isContractor}
													correlationId={row.id}
													onClick={isContractor ? handleOpenStatusEditingDialog : undefined}
												/>
											);
										},
									},
									{
										field: 'actions',
										headerName: 'Actions',
										headerAlign: 'center',
										align: 'right',
										flex: 0.3,
										sortable: false,
										filterable: false,
										resizable: false,
										renderCell: (params) => {
											const {
												row: { id },
											} = params;

											return (
												<IconButton size="large" color="inherit" data-request-id={id} onClick={handleOpenMenu}>
													<Iconify icon={'eva:more-vertical-fill'} />
												</IconButton>
											);
										},
									},
								]}
								rows={requests.map((request) => {
									return {
										id: request.id,
										candidateName: request.candidateName,
										phone: request.candidate.phone,
										testCentre: request.supplier.name,
										assessmentDate: request.assessmentDates[0] ? new Date(request.assessmentDates[0]) : null,
										paymentStatus: request.paymentStatus,
										status: request.status,
										actions: '',
									} as DataGridRow;
								})}
							/>
						</DataGridContainer>

						{/* request menu options popover */}
						<Popover
							open={Boolean(anchorEl)}
							onClose={handleCloseMenu}
							anchorEl={anchorEl}
							anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
							transformOrigin={{ vertical: 'top', horizontal: 'right' }}
							PaperProps={{
								sx: {
									p: 1,
									width: 140,
									'& .MuiMenuItem-root': {
										px: 1,
										typography: 'body2',
										borderRadius: 0.75,
									},
								},
							}}
						>
							<MenuItem onClick={handleOpenRequestDetail}>
								<Iconify icon={'eva:file-text-outline'} sx={{ mr: 1 }} />
								View
							</MenuItem>

							{isContractor || !requestEditable ? null : (
								<MenuItem onClick={handleEditRequest}>
									<Iconify icon={'eva:edit-fill'} sx={{ mr: 1 }} />
									Edit
								</MenuItem>
							)}

							{!isContractor && (
								<MenuItem sx={{ color: 'error.main' }} onClick={handleDeleteRequest}>
									<Iconify icon={'eva:trash-2-outline'} sx={{ mr: 1 }} />
									Delete
								</MenuItem>
							)}
						</Popover>

						{/* request delete confirm */}
						<Dialog
							open={Boolean(requestDeleteConfirmDialogOpen)}
							onClose={handleCloseRequestDeleteConfirmDialog}
							title={'Warning'}
						>
							<Grid container spacing={2} sx={{ m: 2 }}>
								<Grid size={{ md: 12 }}>
									<Typography variant="subtitle2">{`Confirm to delete request ${selectedRequest?.id}`}</Typography>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button loading={isDelReqLoading} variant="outlined" color="primary" onClick={onRequestDeleteConfirm}>
										<span>Confirm</span>
									</Button>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button color="error" variant="outlined" onClick={handleCloseRequestDeleteConfirmDialog}>
										Cancel
									</Button>
								</Grid>
							</Grid>
						</Dialog>

						{/* status editing (CONTRACTOR only) */}
						<Dialog
							open={Boolean(statusEditingDialogOpen)}
							onClose={handleCloseStatusEditingDialog}
							title={`Change request ${selectedRequest?.id} status`}
						>
							<Grid container spacing={2} sx={{ m: 2 }}>
								<Grid size={{ md: 12 }}>
									<NativeSelect
										defaultValue={selectedRequest?.status}
										inputProps={{
											name: 'status',
											id: 'uncontrolled-native',
											onChange: onStatusChange,
										}}
									>
										{['--- Please Select ---', ...statusOptions].map((status, index) => (
											<option key={status} value={status}>
												{status}
											</option>
										))}
									</NativeSelect>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button
										loading={isUpdateReqStatusLoading}
										variant="outlined"
										color="primary"
										onClick={onStatusUpdate}
									>
										<span>Confirm</span>
									</Button>
								</Grid>
								<Grid size={{ md: 6 }}>
									<Button color="error" variant="outlined" onClick={handleCloseStatusEditingDialog}>
										Cancel
									</Button>
								</Grid>
							</Grid>
						</Dialog>
					</>
				)}
			</Box>
		</>
	);
}
